import {
  Button,
  ButtonColor,
  ProfileImage,
  Rating,
} from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/router';
import { type FC, useMemo, useState } from 'react';

import { APPLICANT_BASE_URL } from '../../utils/constants';
import { getCoachProfileUrl } from '../../utils/routing';
import { useCountLines } from '../../utils/use-count-lines';
import OrganizationTag, {
  type OrganizationTagDetails,
} from '../win-wall/reviews/review/OrganizationTag';

import { type ArticleAuthorCoachFragment } from './__generated-gql-types__/ArticleAuthor.generated';

interface ArticleAuthorProps {
  coach: ArticleAuthorCoachFragment;
  categorySlug?: string;
}

export const ArticleAuthor: FC<ArticleAuthorProps> = ({
  coach,
  categorySlug,
}) => {
  const router = useRouter();

  const [bioLines, bioRef] = useCountLines();
  const [expandedBio, setExpandedBio] = useState(false);

  const coachingOutcomes = useMemo(
    () =>
      coach.outcomes
        ?.reduce<Array<OrganizationTagDetails['organization']>>(
          (prev, curr) => {
            if (curr.__typename === 'CareerOutcomeSummary') {
              return [
                ...prev,
                ...curr.companies.map((company) => ({
                  name: company.name,
                  logoLink: company.logoLink,
                })),
              ];
            }
            if (curr.__typename === 'SchoolOutcomeSummary') {
              return [
                ...prev,
                ...curr.schools.map((school) => ({
                  name: school.name,
                  logoLink: school.logoLink,
                })),
              ];
            }

            return prev;
          },
          [],
        )
        .slice(0, 5),
    [coach.outcomes],
  );

  const coachProfileUrl = new URL(APPLICANT_BASE_URL);
  coachProfileUrl.pathname = getCoachProfileUrl(coach.slug, categorySlug);

  return (
    <div className="flex gap-6 border-t border-leland-gray-stroke py-8">
      <ProfileImage
        className="size-16"
        firstName={coach.user.firstName}
        lastName={coach.user.lastName}
        pictureLink={coach.user.pictureLink}
      />
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <h3 className="text-2xl font-medium">
              Written by {coach.user.firstName}
            </h3>
            <Rating totalStars={5} rate={coach.averageReviewRate} showRate />
            <p className="text-leland-gray-light">({coach.reviewTotal})</p>
          </div>
          <p
            ref={bioRef}
            className={`text-lg text-leland-gray-light ${
              !expandedBio && bioLines >= 4 ? 'line-clamp-4' : ''
            }`}
          >
            {coach.bio}
          </p>
          {bioLines > 4 ? (
            <button
              className="link link--primary text-left text-lg"
              onClick={() => setExpandedBio((prev) => !prev)}
              id="article_author--view_more_bio"
            >
              {expandedBio ? 'View less' : 'View more'}
            </button>
          ) : null}
        </div>
        {coachingOutcomes ? (
          <>
            <p className="font-medium text-leland-gray-light">
              {coach.user.firstName} has helped clients get into organizations
              like:
            </p>
            <div className="flex flex-wrap gap-2">
              {coachingOutcomes.map((outcome) => (
                <OrganizationTag
                  key={outcome.name}
                  tagDetails={{
                    organization: outcome,
                  }}
                />
              ))}
            </div>
          </>
        ) : null}
        <div>
          <Button
            label="View Profile"
            buttonColor={ButtonColor.BLACK}
            onClick={() => {
              void router.push(coachProfileUrl);
            }}
            id="article_author--view_coach_profile"
          />
        </div>
      </div>
    </div>
  );
};
